import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Loader2, FileText, Package, ExternalLink } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useUnifiedSearch, useSearchSuggestions } from '@/hooks/useUnifiedSearch';
import type { SearchResult } from '@/services/searchService';

interface SearchBarProps {
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the full search interface */
  showDropdown?: boolean;
}

/**
 * Global search bar component with typeahead and dropdown results
 *
 * Features:
 * - Unified search across products and documentation
 * - Real-time suggestions while typing
 * - Dropdown results with categorized content
 * - Keyboard navigation support
 * - Direct navigation to results or full search page
 */
export function SearchBar({ placeholder, className = '', showDropdown = true }: SearchBarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get localized placeholder text
  const searchPlaceholder = placeholder || t('searchBar.placeholder');

  // Search hooks
  const {
    data: searchResults,
    isLoading: isSearchLoading,
    hasSearched,
  } = useUnifiedSearch(query, {
    enabled: showDropdown && query.length >= 2,
    limit: 5, // Show fewer results in dropdown
  });

  const { data: suggestions, isLoading: isSuggestionsLoading } = useSearchSuggestions(query, {
    enabled: showDropdown && query.length >= 2,
    limit: 3,
  });

  // Combined results for display
  const allResults = useMemo(() => {
    return searchResults ? [...searchResults.products, ...searchResults.documentation] : [];
  }, [searchResults]);

  const totalResults = allResults.length;
  const hasResults = totalResults > 0;
  const isLoading = isSearchLoading || isSuggestionsLoading;

  // Handle input changes
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);
      setSelectedIndex(-1);

      if (showDropdown && value.length >= 2) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    },
    [showDropdown]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (query.trim()) {
        navigate(`/app/search?q=${encodeURIComponent(query.trim())}`);
        setIsOpen(false);
        inputRef.current?.blur();
      }
    },
    [query, navigate]
  );

  // Handle result selection
  const handleResultClick = useCallback(
    (result: SearchResult) => {
      navigate(result.url);
      setIsOpen(false);
      setQuery('');
      inputRef.current?.blur();
    },
    [navigate]
  );

  // Handle suggestion selection
  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setQuery(suggestion);
      setIsOpen(false);
      // Trigger search with the suggestion
      navigate(`/app/search?q=${encodeURIComponent(suggestion)}`);
      inputRef.current?.blur();
    },
    [navigate]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || !showDropdown) return;

      const suggestionCount = suggestions?.suggestions.length || 0;
      const totalItems = suggestionCount + totalResults;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % Math.max(totalItems, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev <= 0 ? totalItems - 1 : prev - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex === -1) {
            handleSubmit(e);
          } else if (selectedIndex < suggestionCount) {
            // Selected a suggestion
            const suggestion = suggestions?.suggestions[selectedIndex];
            if (suggestion) {
              handleSuggestionClick(suggestion);
            }
          } else if (selectedIndex < suggestionCount + totalResults) {
            // Selected a result
            const resultIndex = selectedIndex - suggestionCount;
            const result = allResults[resultIndex];
            if (result) {
              handleResultClick(result);
            }
          }
          break;
        case 'Escape':
          setIsOpen(false);
          inputRef.current?.blur();
          break;
      }
    },
    [
      isOpen,
      showDropdown,
      suggestions?.suggestions,
      totalResults,
      selectedIndex,
      handleSubmit,
      handleSuggestionClick,
      handleResultClick,
      allResults,
    ]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Result rendering helper
  const renderResult = (result: SearchResult, index: number) => {
    const isSelected = selectedIndex === index + (suggestions?.suggestions.length || 0);
    const Icon = result.type === 'product' ? Package : FileText;

    return (
      <div
        key={`${result.type}-${result.id}`}
        className={`flex cursor-pointer items-start gap-3 rounded-md p-3 text-sm hover:bg-muted ${
          isSelected ? 'bg-muted' : ''
        }`}
        onClick={() => handleResultClick(result)}
      >
        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span className="truncate font-medium">{result.title}</span>
            <Badge variant="secondary" className="text-xs">
              {result.type === 'product' ? t('badges.product') : t('badges.docs')}
            </Badge>
          </div>

          {/* Additional metadata for products */}
          {result.type === 'product' && result.metadata && (
            <div className="mb-1 flex flex-wrap gap-2">
              {result.metadata.sku && (
                <span className="rounded bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground">
                  {t('metadata.sku')}: {result.metadata.sku}
                </span>
              )}
              {result.metadata.brand && (
                <span className="rounded bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground">
                  {result.metadata.brand}
                </span>
              )}
              {result.metadata.category_name && (
                <span className="rounded bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground">
                  {result.metadata.category_name}
                </span>
              )}
            </div>
          )}

          {/* Additional metadata for documentation */}
          {result.type === 'documentation' && result.metadata && (
            <div className="mb-1 flex flex-wrap gap-2">
              {result.metadata.language && (
                <span className="rounded bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground">
                  {result.metadata.language.toUpperCase()}
                </span>
              )}
              {result.metadata.word_count && (
                <span className="rounded bg-muted/50 px-2 py-0.5 text-xs text-muted-foreground">
                  {result.metadata.word_count} {t('metadata.wordCount')}
                </span>
              )}
            </div>
          )}

          {result.snippet && (
            <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
              {result.snippet}
            </p>
          )}
        </div>
        <ExternalLink className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
      </div>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <Popover open={isOpen && showDropdown} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <form onSubmit={handleSubmit} className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="h-11 w-[460px] rounded-lg border-2 border-border bg-background pl-10 pr-12 text-sm placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 focus-visible:ring-primary/20"
              onFocus={() => {
                if (showDropdown && query.length >= 2) {
                  setIsOpen(true);
                }
              }}
            />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </form>
        </PopoverTrigger>

        <PopoverContent
          className="w-[460px] p-0"
          align="start"
          onOpenAutoFocus={e => e.preventDefault()}
        >
          <div className="max-h-96 overflow-y-auto">
            {/* Suggestions Section */}
            {suggestions?.suggestions && suggestions.suggestions.length > 0 && (
              <div className="border-b border-border p-3">
                <p className="mb-2 text-xs font-medium text-muted-foreground">
                  {t('searchBar.suggestions')}
                </p>
                <div className="space-y-1">
                  {suggestions.suggestions.map((suggestion, index) => (
                    <Button
                      key={suggestion}
                      variant="ghost"
                      size="sm"
                      className={`w-full justify-start text-sm ${
                        selectedIndex === index ? 'bg-muted' : ''
                      }`}
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <Search className="mr-2 size-3" />
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Results Section */}
            {hasResults && (
              <>
                {suggestions?.suggestions && suggestions.suggestions.length > 0 && <Separator />}
                <div className="p-3">
                  <p className="mb-3 text-xs font-medium text-muted-foreground">
                    {t('searchResults.resultsFound')} ({totalResults})
                  </p>
                  <div className="space-y-2">
                    {allResults.map((result, index) => renderResult(result, index))}
                  </div>
                </div>
              </>
            )}

            {/* No results message */}
            {hasSearched && !isLoading && !hasResults && (
              <div className="p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {t('searchBar.noResults')} "{query}"
                </p>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
